import { Capacitor } from "@capacitor/core";
import {
  SecureStorage,
  StorageError,
} from "@aparajita/capacitor-secure-storage";
import * as Sentry from "@sentry/browser";

const isNative = Capacitor.isNativePlatform();

const memoryFallback = new Map<string, string>();

/** Key used only to probe whether web storage is readable; never written. */
const WEB_STORAGE_PROBE_KEY = "__saile_storage_probe__";

type WebStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

let cachedWebStorage: WebStorage | null | undefined;

/**
 * Some embedded browser contexts do not give us web storage at all: Apple Mail's
 * link preview renderer omits the `localStorage` binding entirely (so a bare
 * reference throws ReferenceError rather than evaluating to undefined), and
 * sandboxed iframes or Safari with site data blocked throw SecurityError the
 * first time the property is touched. That is expected environment behavior
 * rather than a bug, so callers silently degrade to `memoryFallback` instead of
 * reporting to Sentry.
 *
 * The result is memoized because availability cannot change within a page load,
 * and probing on every read would be wasteful.
 */
export const getWebStorage = (): WebStorage | null => {
  if (cachedWebStorage !== undefined) {
    return cachedWebStorage;
  }
  cachedWebStorage = probeWebStorage();
  return cachedWebStorage;
};

const probeWebStorage = (): WebStorage | null => {
  try {
    // `typeof` must come first: it is the only reference form that tolerates an
    // undeclared identifier.
    if (typeof localStorage === "undefined" || localStorage === null) {
      return null;
    }
    // Touching the storage object is what throws when site data is blocked, so
    // do a read before handing it to callers.
    localStorage.getItem(WEB_STORAGE_PROBE_KEY);
    return localStorage;
  } catch {
    return null;
  }
};

const reportStorageError = (
  op: "getItem" | "setItem" | "removeItem",
  key: string,
  error: unknown,
  extra?: Record<string, unknown>,
) => {
  const code = error instanceof StorageError ? error.code : undefined;
  Sentry.captureException(error, {
    tags: {
      storage_op: op,
      storage_key: key,
      storage_native: String(isNative),
      storage_platform: Capacitor.getPlatform(),
      storage_error_code: code ?? "unknown",
    },
    extra,
  });
};

export class Storage {
  static async getItem(key: string): Promise<string | null> {
    try {
      if (isNative) {
        return await SecureStorage.getItem(key);
      }
      const webStorage = getWebStorage();
      if (!webStorage) {
        return memoryFallback.get(key) ?? null;
      }
      return webStorage.getItem(key);
    } catch (error) {
      reportStorageError("getItem", key, error);
      return memoryFallback.get(key) ?? null;
    }
  }

  static async setItem(key: string, value: string): Promise<void> {
    memoryFallback.set(key, value);
    try {
      if (isNative) {
        await SecureStorage.setItem(key, value);
        return;
      }
      // When web storage is unavailable the in-memory write above is all we can
      // do, and it already happened.
      getWebStorage()?.setItem(key, value);
    } catch (error) {
      reportStorageError("setItem", key, error, { length: value.length });
    }
  }

  static async removeItem(key: string): Promise<void> {
    memoryFallback.delete(key);
    try {
      if (isNative) {
        await SecureStorage.removeItem(key);
        return;
      }
      getWebStorage()?.removeItem(key);
    } catch (error) {
      reportStorageError("removeItem", key, error);
    }
  }
}
